import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ANALYTIC_EVENTS, Analytics } from 'analytics/Analytics';
import SecureIcon from 'assets/icons/base/secure-icon.svg';
import { useComment, useIsMobile, usePayment, useTranslate, useValidate } from 'hooks';
import { useAmount } from 'hooks/useAmount';
import { useAppSelector } from 'store/hooks';
import { clearPhoneMask } from 'utils';
import { Button, Input, OfferLink } from 'views/components/base/ui';

type PhoneFormFieldsType = {
  payoutPhone: '';
};

const PhoneForm = () => {
  const { paymentData, acquiring, isPayment, fullAmount } = useAppSelector(
    (state) => state.payment,
  );
  const { fetchSendPayment, isLoading } = usePayment();
  const { isMobile } = useIsMobile();
  const { currentAmountForPayment, isValidCurrentAmount } = useAmount();
  const { comment, isValidComment } = useComment();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<PhoneFormFieldsType>({
    mode: 'onChange',
    defaultValues: {
      payoutPhone: '',
    },
  });

  const { t } = useTranslate();
  const { validatePlus24Login } = useValidate();

  useEffect(() => {
    Analytics.sentEvent(ANALYTIC_EVENTS.paymentFormPayoutPhone);
  }, []);

  const sendPayment = async (data: PhoneFormFieldsType) => {
    const payData = {
      phone: '',
      email: '',
      description: comment,
      ID: paymentData?.ID,
      acquiringID: acquiring?.ID,
      amount: currentAmountForPayment,
      payoutPhone: clearPhoneMask(data.payoutPhone),
    };

    await fetchSendPayment(payData);
  };

  return (
    <form onSubmit={handleSubmit(sendPayment)} className="md:max-w-[366px] flex flex-col">
      <div className="my-5 md:my-7 gap-6 lg:gap-0 flex flex-col lg:flex-row md:max-w-none">
        <Controller
          name="payoutPhone"
          control={control}
          rules={{
            required: true,
            validate: validatePlus24Login,
          }}
          render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
            <Input
              label={t('enter_phone_number')}
              placeholder="+7 (xxx) xxx xx xx"
              mask=" "
              name="payoutPhone"
              format="+7 (###) ### ## ##"
              isMobile={!!isMobile}
              containerClassName="flex-shrink-0 w-full"
              value={value}
              ref={ref}
              onChange={onChange}
              type="tel"
              error={error?.message}
            />
          )}
        />
      </div>
      <div className="text-[10px] font-normal leading-3 text-dark/60 flex items-center gap-2">
        <img src={SecureIcon} width={18} height={18} alt="secure" />{' '}
        {t('lang.payout_to_phone_number.info')}
      </div>
      <div className="mt-auto md:mt-8">
        <Button
          isLoading={isLoading}
          className="mb-2 md:max-w-[224px]"
          disabled={!isValid || !isValidCurrentAmount || !isValidComment}
          onClick={handleSubmit(sendPayment)}
        >
          {isPayment ? t('pay') : t('pay_out')}{' '}
          {isMobile && fullAmount && isValid ? `${fullAmount} KZT` : null}
        </Button>
        <OfferLink />
      </div>
    </form>
  );
};

export { PhoneForm };
